import React from 'react';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Separator } from '../../components/ui/separator';
import { sanitizeHtml } from '../../lib/sanitize';
import {
  Reply,
  Forward,
  Trash2,
  Star,
  MoreVertical,
  Paperclip,
  Download,
  X,
  Archive,
  Flag,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { cn } from '../../lib/utils';

export const EmailDetail = ({ email, onClose, onReply, onDelete, onToggleFlag }) => {
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInitials = (emailAddr) => {
    if (!emailAddr) return '?';
    // Extract display name or local part before @
    // Handle "Name <addr@domain>" format
    const nameMatch = emailAddr.match(/^([^<]+?)\s*</);
    const name = nameMatch ? nameMatch[1].trim() : emailAddr.split('@')[0];
    if (!name) return '?';
    // Use spread to properly iterate over Unicode code points (handles CJK, Cyrillic, Arabic…)
    const codePoints = [...name.replace(/\s+/g, ' ').trim()];
    if (codePoints.length === 0) return '?';
    // Try to get initials from first two words, otherwise first two code points
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return ([...words[0]][0] + [...words[1]][0]).toUpperCase();
    }
    return (codePoints[0] + (codePoints[1] || '')).toUpperCase();
  };

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onReply} data-testid="reply-btn">
            <Reply className="w-4 h-4 mr-2" />
            Répondre
          </Button>
          <Button variant="outline" size="sm" data-testid="forward-btn">
            <Forward className="w-4 h-4 mr-2" />
            Transférer
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleFlag}
            data-testid="flag-email-btn"
          >
            <Star className={cn(
              "w-5 h-5",
              email.is_flagged && "fill-yellow-400 text-yellow-400"
            )} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onDelete}
            data-testid="delete-email-btn"
          >
            <Trash2 className="w-5 h-5" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>
                <Archive className="w-4 h-4 mr-2" />
                Archiver
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Flag className="w-4 h-4 mr-2" />
                Marquer comme spam
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Separator orientation="vertical" className="h-6 mx-2" />
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Email content */}
      <ScrollArea className="flex-1">
        <div className="p-6">
          {/* Subject */}
          <h1 className="text-2xl font-semibold font-['Plus_Jakarta_Sans'] mb-6">
            {email.subject || '(Sans objet)'}
          </h1>

          {/* Sender info */}
          <div className="flex items-start gap-4 mb-6">
            <Avatar className="w-12 h-12">
              <AvatarFallback className="bg-primary/10 text-primary">
                {getInitials(email.from_address)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{email.from_address}</p>
                  <p className="text-sm text-muted-foreground">
                    À: {email.to_addresses?.join(', ')}
                  </p>
                  {email.cc_addresses?.length > 0 && (
                    <p className="text-sm text-muted-foreground">
                      Cc: {email.cc_addresses.join(', ')}
                    </p>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {formatDate(email.date)}
                </p>
              </div>
            </div>
          </div>

          <Separator className="my-6" />

          {/* Attachments */}
          {email.attachments?.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Paperclip className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">
                  {email.attachments.length} pièce(s) jointe(s)
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {email.attachments.map((attachment, index) => {
                  const token = localStorage.getItem('promail_token') || '';
                  // Use email-specific endpoint for IMAP-synced attachments
                  const downloadUrl = `${process.env.REACT_APP_BACKEND_URL || ''}/api/emails/${email.id}/attachment/${index}?token=${token}`;
                  return (
                    <a
                      key={index}
                      href={downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex"
                      data-testid={`download-attachment-${index}`}
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        type="button"
                      >
                        <Download className="w-4 h-4" />
                        {attachment.filename}
                      </Button>
                    </a>
                  );
                })}
              </div>
              <Separator className="my-6" />
            </div>
          )}

          {/* Email body */}
          <div className="email-body-content">
            {email.body_html ? (
              <div
                // Bug fix: sanitize HTML to prevent XSS from malicious email bodies
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(email.body_html) }}
                className="text-sm"
              />
            ) : (
              <pre className="whitespace-pre-wrap font-sans text-sm">
                {email.body_text}
              </pre>
            )}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

export default EmailDetail;
